import { copyText } from '../utils/clipboard';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ControlledDeliveryWorkflow from '../components/ControlledDeliveryWorkflow';
import InventoryReleaseRequestPanel from '../components/InventoryReleaseRequestPanel';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');
const fmtDate = v => v ? new Date(v).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const DELIVERY_TYPES = {
  fez: { label:'Fez Logistics', color:'#3B5BDB', bg:'#E7F5FF' },
  public_transport: { label:'Public Transport', color:'#8B5E0A', bg:'#FFF3DC' },
  same_city_rider: { label:'Same City Rider', color:'#2E6B3A', bg:'#E8F5EB' },
  customer_arranged_rider: { label:'Customer-Arranged Rider', color:'#8B5E0A', bg:'#FFF3DC' },
  pending_arrangement: { label:'Arrangement Pending', color:'#8A8A8A', bg:'#F5F5F5' },
  pickup: { label:'Pick Up', color:'#556B2F', bg:'#F2EFE7' },
};

const STATUS_COLORS = {
  pending: { color:'#8A8A8A', bg:'#F5F5F5', label:'Pending' },
  pending_arrangement: { color:'#8A8A8A', bg:'#F5F5F5', label:'Arrangement Pending' },
  preparing: { color:'#8B5E0A', bg:'#FFF3DC', label:'Preparing Delivery' },
  handover_requested: { color:'#3B5BDB', bg:'#E7F5FF', label:'Inventory Handover Requested' },
  handover_accepted: { color:'#2E6B3A', bg:'#E8F5EB', label:'Package Accepted' },
  dispatched: { color:'#556B2F', bg:'#F2EFE7', label:'Dispatched' },
  delivery_attempt_failed: { color:'#8F2E24', bg:'#FDEAEA', label:'Delivery Attempt Failed' },
  awaiting_proof: { color:'#8B5E0A', bg:'#FFF3DC', label:'Awaiting Proof' },
  awaiting_completion: { color:'#3B5BDB', bg:'#E7F5FF', label:'Awaiting Completion' },
  ready_for_pickup: { color:'#556B2F', bg:'#F2EFE7', label:'Ready for Pickup' },
  balance_override_pending: { color:'#8F2E24', bg:'#FDEAEA', label:'MD Override Pending' },
  booked: { color:'#3B5BDB', bg:'#E7F5FF', label:'Delivery Booked' },
  rider_picked_up: { color:'#2E6B3A', bg:'#E8F5EB', label:'Rider Picked Up' },
  at_sorting_center: { color:'#8B5E0A', bg:'#FFF3DC', label:'At Sorting Center' },
  on_the_way: { color:'#556B2F', bg:'#F2EFE7', label:'On the Way to Last Mile' },
  at_last_mile_hub: { color:'#A8893D', bg:'#FDF3D9', label:'At Last Mile Hub' },
  out_for_delivery: { color:'#8B5E0A', bg:'#FFF3DC', label:'Out for Delivery' },
  with_driver: { color:'#3B5BDB', bg:'#E7F5FF', label:'With Driver' },
  rider_picked_up: { color:'#2E6B3A', bg:'#E8F5EB', label:'Rider Picked Up' },
  arrived: { color:'#556B2F', bg:'#F2EFE7', label:'Arrived' },
  delivered: { color:'#2E6B3A', bg:'#D3F9D8', label:'Delivered' },
  picked_up: { color:'#2E6B3A', bg:'#D3F9D8', label:'Picked Up' },
};

const STATUS_OPTIONS = {
  fez: [
    { value:'booked', label:'Delivery Booked with Fez' },
    { value:'rider_picked_up', label:'Rider Picked Up from Office' },
    { value:'at_sorting_center', label:'Package at Sorting Center' },
    { value:'on_the_way', label:'On the Way to Last Mile Hub' },
    { value:'at_last_mile_hub', label:'Arrived at Last Mile Hub' },
    { value:'out_for_delivery', label:'Out for Delivery' },
    { value:'delivered', label:'Successfully Delivered' },
  ],
  public_transport: [
    { value:'out_for_delivery', label:'Out for Delivery — staff will contact customer' },
    { value:'with_driver', label:'With Driver — add driver details' },
    { value:'arrived', label:'Driver Arrived at Customer City' },
    { value:'delivered', label:'Successfully Delivered' },
  ],
  same_city_rider: [
    { value:'rider_picked_up', label:'Rider Picked Up from Office' },
    { value:'delivered', label:'Successfully Delivered' },
  ],
  pickup: [
    { value:'picked_up', label:'Picked Up by Customer' },
  ],
};

const DEFAULT_MESSAGES = {
  booked: 'Your delivery has been booked with Fez Delivery. You will receive updates as your package moves.',
  at_sorting_center: 'Your package has arrived at the Fez sorting center and is being processed for delivery.',
  on_the_way: 'Your package is on its way to the last mile delivery hub closest to you.',
  at_last_mile_hub: 'Your package has arrived at the last mile hub and will be delivered to you soon.',
  out_for_delivery: 'Your package is on the way to your delivery address and will arrive shortly.',
  with_driver: 'Your package has been handed to a driver who will bring it to you.',
  rider_picked_up: 'A rider has picked up your package from our office and is on the way to you.',
  arrived: 'Your driver has arrived at your city. Please call the driver to collect your package.',
  delivered: 'Your package has been successfully delivered. Thank you for shopping with Bloans!',
  picked_up: 'Your package has been successfully picked up. Thank you for shopping with Bloans!',
};

export default function Deliveries() {
  const { user } = useAuth();
  const location = useLocation();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showEditFee, setShowEditFee] = useState(false);
  const [showEditDelivery, setShowEditDelivery] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [feeForm, setFeeForm] = useState({delivery_fee:'',driver_amount:'',delivery_fee_paid_by:'customer',rider_name:'',rider_phone:''});
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [jobOrders, setJobOrders] = useState([]);
  const [storageFee, setStorageFee] = useState(null);
  const [showReadyNotice, setShowReadyNotice] = useState(false);
  const [readyPreview, setReadyPreview] = useState(null);
  const [readyOptions, setReadyOptions] = useState({
    arrangement_confirmed:false,
    delivery_fee_paid:false
  });
  const [readyBusy, setReadyBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [form, setForm] = useState({
    customer_name:'', customer_email:'', customer_phone:'',
    delivery_address:'', delivery_type:'public_transport',
    fez_tracking_code:'', fez_tracking_url:'',
    balance_amount:'', notes:'', rider_name:'', rider_phone:'',
    driver_amount:'', package_weight:'', package_weight_unit:'kg',
    package_description:'', delivery_fee:'', delivery_fee_paid_by:'customer',
    order_id:'', invoice_id:'', job_order_id:''
  });

  const [updateForm, setUpdateForm] = useState({
    status:'', message:'', driver_name:'', driver_phone:'',
    pickup_person_name:'', pickup_person_phone:''
  });

  const load = () => {
    setLoading(true);
    get('/deliveries').then(d => setDeliveries(d.data||[])).finally(() => setLoading(false));
  };

  const loadDetail = async (id) => {
    const d = await get('/deliveries/'+id);
    setSelected(d.data);
    // Load storage fee
    get('/deliveries/'+id+'/storage-fee').then(f => setStorageFee(f)).catch(()=>{});
  };

  useEffect(() => {
    load();
    get('/invoices?status=unpaid,part_paid').then(d => setInvoices(d.data||[])).catch(()=>{});
    get('/job-orders?status=ready').then(d => setJobOrders(d.data||[])).catch(()=>{});
    const params = new URLSearchParams(location.search);
    const urlType = params.get('type');
    const urlStatus = params.get('status');
    if (urlType) setFilterType(urlType);
    if (urlStatus === 'in_transit') setFilterStatus('out_for_delivery');
    else if (urlStatus === 'completed') setFilterStatus('delivered');
    else if (urlStatus === 'pending') setFilterStatus('pending');
    else if (urlStatus === 'storage_fee') setFilterStatus('storage_fee');
    else if (urlStatus === 'uncontacted') setFilterStatus('uncontacted');
    else if (urlStatus) setFilterStatus(urlStatus);
  }, [location.search]);

  const createDelivery = async () => {
    if (!form.customer_name || !form.delivery_type) { alert('Customer name and delivery type required'); return; }
    setSaving(true);
    try {
      const result = await post('/deliveries', form);
      alert('Delivery created! Tracking code: ' + result.data.tracking_code + '\nTracking URL: https://track.bloanshandbags.com/' + result.data.tracking_code);
      setShowForm(false);
      setForm({customer_name:'',customer_email:'',customer_phone:'',delivery_address:'',delivery_type:'public_transport',fez_tracking_code:'',fez_tracking_url:'',balance_amount:'',notes:'',rider_name:'',rider_phone:'',driver_amount:'',package_weight:'',package_weight_unit:'kg',package_description:'',delivery_fee:'',delivery_fee_paid_by:'customer',order_id:'',invoice_id:''});
      load();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const addUpdate = async () => {
    if (!updateForm.status || !updateForm.message) { alert('Status and message required'); return; }
    setSaving(true);
    try {
      await post('/deliveries/'+selected.id+'/update', updateForm);
      setShowUpdate(false);
      setUpdateForm({status:'',message:'',driver_name:'',driver_phone:'',pickup_person_name:'',pickup_person_phone:''});
      loadDetail(selected.id);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const markContacted = async () => {
    if (!window.confirm('Mark customer as contacted? This starts the 7-day grace period for storage fees.')) return;
    try {
      await post('/deliveries/'+selected.id+'/contact-customer', {});
      alert('Customer marked as contacted. 7-day grace period started.');
      loadDetail(selected.id);
    } catch(e) { alert(e.message); }
  };

  const confirmBalance = async () => {
    if (!window.confirm('Confirm that balance payment has been received?')) return;
    try {
      await put('/deliveries/'+selected.id+'/confirm-balance', {});
      alert('Balance confirmed.');
      loadDetail(selected.id);
    } catch(e) { alert(e.message); }
  };

  const loadReadyPreview = async options => {
    const response = await post(
      '/deliveries/' +
        selected.id +
        '/ready-notification-preview',
      {
          arrangement_confirmed:false,
          delivery_fee_paid:false
        }
    );

    setReadyPreview(response.data);
    return response.data;
  };

  const openReadyNotification = async () => {
    const options = {
      arrangement_confirmed:false,
      delivery_fee_paid:false
    };

    setReadyOptions(options);
    setReadyPreview(null);
    setShowReadyNotice(true);
    setReadyBusy(true);

    try {
      await loadReadyPreview(options);
    } catch (error) {
      setShowReadyNotice(false);
      alert(
        error.message ||
        'Notification preview could not be loaded.'
      );
    } finally {
      setReadyBusy(false);
    }
  };

  const updateReadyOption = async (key, value) => {
    const options = {
      ...readyOptions,
      [key]: value
    };

    setReadyOptions(options);
    setReadyBusy(true);

    try {
      await loadReadyPreview(options);
    } catch (error) {
      alert(
        error.message ||
        'Notification preview could not be updated.'
      );
    } finally {
      setReadyBusy(false);
    }
  };

  const sendReadyEmail = async () => {
    if (
      !window.confirm(
        'Send this order-ready message by email?'
      )
    ) return;

    setReadyBusy(true);

    try {
      const response = await post(
        '/deliveries/' +
          selected.id +
          '/send-ready-email',
        {
          arrangement_confirmed:false,
          delivery_fee_paid:false
        }
      );

      alert(
        response.message ||
        'Email accepted by the mail server.'
      );

      setShowReadyNotice(false);
      await loadDetail(selected.id);
      load();
    } catch (error) {
      alert(
        error.message ||
        'The email could not be sent.'
      );
    } finally {
      setReadyBusy(false);
    }
  };

  const confirmReadyWhatsApp = async () => {
    if (
      !window.confirm(
        'Confirm only after the WhatsApp message has actually been sent. This starts the 7-day storage grace period.'
      )
    ) return;

    setReadyBusy(true);

    try {
      const response = await post(
        '/deliveries/' +
          selected.id +
          '/record-ready-whatsapp',
        {
          arrangement_confirmed:false,
          delivery_fee_paid:false
        }
      );

      alert(
        response.message ||
        'WhatsApp notification recorded.'
      );

      setShowReadyNotice(false);
      await loadDetail(selected.id);
      load();
    } catch (error) {
      alert(
        error.message ||
        'WhatsApp sending could not be recorded.'
      );
    } finally {
      setReadyBusy(false);
    }
  };

  const filtered = deliveries.filter(d => {
    const matchSearch = !search || d.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.tracking_code?.toLowerCase().includes(search.toLowerCase());
    const matchType = !filterType || d.delivery_type === filterType;
    const matchStatus = !filterStatus || d.status === filterStatus;
    return matchSearch && matchType && matchStatus;
  });

  // ORDER-READY FIRST-STEP WORKFLOW
  const orderReadyNoticeRecorded =
    Boolean(
      selected?.ready_notification_sent_at ||
      selected?.whatsapp_notification_sent_at
    );

  const inp = 'form-control';
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setUpd = (k,v) => setUpdateForm(f=>({...f,[k]:v}));

  if (selected) return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={()=>{setSelected(null);setStorageFee(null);}}>← Back</button>
          <h2 className="page-title" style={{marginTop:8}}>Delivery — {selected.tracking_code}</h2>
        </div>
        <div style={{display:'flex',gap:8}}>
          {['public_transport','same_city_rider','pickup'].includes(selected.delivery_type) &&
            (user?.role === 'super_admin' ||
              (!selected.delivered_at && !selected.pickup_at &&
               !['delivered','picked_up'].includes(selected.status))) && (
            <button className="btn btn-outline btn-sm" style={{fontSize:12,color:'#556B2F',borderColor:'#556B2F'}}
              onClick={()=>{
                setFeeForm({
                  delivery_fee: selected.delivery_fee||'',
                  driver_amount: selected.driver_amount||'',
                  delivery_fee_paid_by: selected.delivery_fee_paid_by||'customer',
                  rider_name: selected.rider_name||'',
                  rider_phone: selected.rider_phone||''
                });
                setShowEditFee(true);
              }}>Edit Fee & Driver Amount</button>
          )}
          <button className="btn btn-outline btn-sm" style={{fontSize:12,color:'#3B2A21',borderColor:'#3B2A21'}}
            onClick={()=>printWaybill(selected)}>🖨️ Print Waybill</button>
          {selected.is_legacy === true && (
            <>
          {!['delivered','picked_up'].includes(selected.status) && (
            <button
              className="btn btn-primary btn-sm"
              onClick={openReadyNotification}
            >
              📣 Order Ready Notice
            </button>
          )}
          {!selected.customer_contacted_at && !['delivered','picked_up'].includes(selected.status) && (
            <button className="btn btn-outline btn-sm" style={{color:'#8B5E0A',borderColor:'#8B5E0A'}}
              onClick={markContacted}>📞 Mark Customer Contacted</button>
          )}
          {selected.balance_amount && !selected.balance_confirmed_at &&
            ['super_admin','sales_manager'].includes(user?.role) && (
            <button className="btn btn-outline btn-sm" style={{color:'#2E6B3A',borderColor:'#2E6B3A'}}
              onClick={confirmBalance}>✅ Confirm Balance Paid</button>
          )}
          {selected.is_legacy === true && !['delivered','picked_up'].includes(selected.status) && (
            <button className="btn btn-primary btn-sm" onClick={()=>{
              setUpdateForm({status:'',message:'',driver_name:'',driver_phone:'',pickup_person_name:'',pickup_person_phone:''});
              setShowUpdate(true);
            }}>+ Add Update</button>
          )}
          <button className="btn btn-outline btn-sm" style={{fontSize:12,color:'#3B2A21',borderColor:'#3B2A21'}}
            onClick={()=>{
              setEditForm({
                customer_name: selected.customer_name||'',
                customer_phone: selected.customer_phone||'',
                customer_email: selected.customer_email||'',
                delivery_address: selected.delivery_address||'',
                delivery_type: selected.delivery_type||'public_transport',
                delivery_fee_paid_by: selected.delivery_fee_paid_by||'customer',
                balance_amount: selected.balance_amount||'',
                notes: selected.notes||''
              });
              setShowEditDelivery(true);
            }}>✏️ Edit Delivery</button>
            </>
          )}
        </div>
      </div>

      {selected.is_legacy !== true &&
        !orderReadyNoticeRecorded &&
        ![
          'dispatched',
          'out_for_delivery',
          'delivered',
          'picked_up',
          'cancelled'
        ].includes(selected.status) && (
          <div
            style={{
              background:'#FFF3DC',
              border:'2px solid #A8893D',
              borderRadius:12,
              padding:20,
              marginBottom:16
            }}
          >
            <div
              style={{
                fontSize:12,
                fontWeight:800,
                color:'#8B5E0A',
                textTransform:'uppercase',
                letterSpacing:'.08em',
                marginBottom:8
              }}
            >
              Step 1 - Customer Notification Required
            </div>

            <div
              style={{
                fontSize:16,
                fontWeight:700,
                color:'#3B2A21',
                marginBottom:8
              }}
            >
              Notify the customer that the order is ready
            </div>

            <div
              style={{
                fontSize:13,
                lineHeight:1.6,
                color:'#6B5748',
                marginBottom:14
              }}
            >
              BloansBooks will use the current invoice
              balance. When money is outstanding, the
              message will request payment before delivery
              arrangements begin.
            </div>

            <button
              className="btn btn-primary"
              onClick={openReadyNotification}
            >
              Notify Customer That Order Is Ready
            </button>

            <div
              style={{
                fontSize:12,
                color:'#8B5E0A',
                marginTop:10
              }}
            >
              Availability and delivery arrangement remain
              locked until WhatsApp is confirmed as sent or
              the email is successfully accepted.
            </div>
          </div>
        )}

      {selected.is_legacy !== true && orderReadyNoticeRecorded && !selected.whatsapp_notification_sent_at && !['delivered','picked_up','cancelled'].includes(selected.status) && (
        <div style={{marginBottom:16}}>
          <button className="btn btn-outline" onClick={openReadyNotification}>Send Order-Ready WhatsApp Follow-up</button>
        </div>
      )}

      {selected.is_legacy !== true &&
        orderReadyNoticeRecorded && (
        <>
          <InventoryReleaseRequestPanel
            delivery={selected}
            user={user}
            onRefresh={async () => {
              await loadDetail(selected.id);
              load();
            }}
          />

          <ControlledDeliveryWorkflow
          key={[
            selected.id,
            selected.status,
            selected.handover_status,
            selected.customer_availability_status,
            selected.arrangement_confirmed
          ].join(':')}
          delivery={selected}
          user={user}
          onRefresh={async () => {
            await loadDetail(selected.id);
            load();
          }}
          />
        </>
      )}

      {/* Storage fee alert */}
      {storageFee && storageFee.storage_fee > 0 && (
        <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:14,marginBottom:16,fontSize:13,color:'#8F2E24'}}>
          ⚠ <strong>Storage Fee:</strong> {fmt(storageFee.storage_fee)} ({storageFee.billable_days} day{storageFee.billable_days>1?'s':''} at {fmt(storageFee.fee_per_day)}/day)
        </div>
      )}
      {storageFee && storageFee.grace_days_remaining > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,padding:14,marginBottom:16,fontSize:13,color:'#8B5E0A'}}>
          ⏳ {storageFee.grace_days_remaining} grace day{storageFee.grace_days_remaining>1?'s':''} remaining before storage fees apply
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
        {/* Delivery Info */}
        <div style={{background:'#fff',borderRadius:12,padding:20,border:'1px solid #E8E0D4'}}>
          <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12}}>Delivery Information</div>
          {[
            ['Customer', selected.customer_name],
            ['Email', selected.customer_email||'—'],
            ['Phone', selected.customer_phone||'—'],
            ['Address', selected.delivery_address||'—'],
            ['Type', DELIVERY_TYPES[selected.delivery_type]?.label],
            ['Status', STATUS_COLORS[selected.status]?.label||selected.status],
            ['Tracking Code', selected.tracking_code],
            ['Tracking URL', `https://track.bloanshandbags.com/${selected.tracking_code}`],
            ['Invoice', selected.invoice_id ? selected.invoice_number||selected.invoice_id.slice(0,8) : '—'],
            ['Job Order', selected.job_order_id ? selected.job_order_number||selected.job_order_id.slice(0,8) : '—'],
            ['Created', fmtDate(selected.created_at)],
          ].map(([label,val])=>(
            <div key={label} style={{display:'flex',padding:'6px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{width:'40%',color:'#8A8A8A',fontSize:11,fontWeight:600,textTransform:'uppercase'}}>{label}</div>
              <div style={{flex:1,color:'#2C1F16',wordBreak:'break-all'}}>{val}</div>
            </div>
          ))}
          {selected.fez_tracking_url && (
            <div style={{marginTop:10}}>
              <a href={selected.fez_tracking_url} target="_blank" rel="noopener noreferrer"
                style={{color:'#556B2F',fontSize:13}}>🔗 Fez Tracking Link</a>
            </div>
          )}
        </div>

        {/* Package & Payment Info */}
        <div style={{background:'#fff',borderRadius:12,padding:20,border:'1px solid #E8E0D4'}}>
          <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12}}>Package & Payment</div>
          {[
            ['Description', selected.package_description||'—'],
            ['Weight', selected.package_weight ? `${selected.package_weight} ${selected.package_weight_unit}` : '—'],
            ['Delivery Fee', selected.delivery_fee ? fmt(selected.delivery_fee) : '—'],
            ['Fee Paid By', selected.delivery_fee_paid_by||'—'],
            ['Balance Due', selected.balance_amount ? fmt(selected.balance_amount) : '—'],
            ['Balance Confirmed', selected.balance_confirmed_at ? fmtDate(selected.balance_confirmed_at) : 'Not yet confirmed'],
            ['Customer Contacted', selected.customer_contacted_at ? fmtDate(selected.customer_contacted_at) : 'Not yet contacted'],
          ].map(([label,val])=>(
            <div key={label} style={{display:'flex',padding:'6px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
              <div style={{width:'45%',color:'#8A8A8A',fontSize:11,fontWeight:600,textTransform:'uppercase'}}>{label}</div>
              <div style={{flex:1,color:'#2C1F16'}}>{val}</div>
            </div>
          ))}
          {selected.rider_name && (
            <>
              <div style={{display:'flex',padding:'6px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
                <div style={{width:'45%',color:'#8A8A8A',fontSize:11,fontWeight:600,textTransform:'uppercase'}}>Rider</div>
                <div style={{flex:1,color:'#2C1F16'}}>{selected.rider_name} · {selected.rider_phone}</div>
              </div>
              {selected.driver_amount && (
                <div style={{display:'flex',padding:'6px 0',borderBottom:'1px solid #F2EFE7',fontSize:13}}>
                  <div style={{width:'45%',color:'#8A8A8A',fontSize:11,fontWeight:600,textTransform:'uppercase'}}>Amount to Pay Rider</div>
                  <div style={{flex:1,color:'#2C1F16'}}>{fmt(selected.driver_amount)}</div>
                </div>
              )}
            </>
          )}
          {selected.package_photo_url && (
            <div style={{marginTop:12}}>
              <img src={selected.package_photo_url} alt="Package"
                style={{width:'100%',borderRadius:8,maxHeight:200,objectFit:'cover'}} />
            </div>
          )}
        </div>
      </div>

      {/* Updates Timeline */}
      <div style={{background:'#fff',borderRadius:12,padding:20,border:'1px solid #E8E0D4'}}>
        <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:16}}>Delivery Updates</div>
        {(!selected.updates || selected.updates.length===0) ? (
          <p style={{color:'#8A8A8A',fontSize:13}}>No updates yet.</p>
        ) : (
          <div style={{position:'relative'}}>
            <div style={{position:'absolute',left:16,top:0,bottom:0,width:2,background:'#E8E0D4'}}/>
            {selected.updates.map((u,i)=>(
              <div key={u.id||i} style={{display:'flex',gap:16,marginBottom:20,position:'relative'}}>
                <div style={{width:32,height:32,borderRadius:'50%',background:STATUS_COLORS[u.status]?.bg||'#F2EFE7',
                  border:'2px solid '+(STATUS_COLORS[u.status]?.color||'#556B2F'),
                  display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,zIndex:1,fontSize:14}}>
                  {u.status==='delivered'||u.status==='picked_up'?'✅':
                   u.status==='with_driver'||u.status==='rider_picked_up'?'🚗':
                   u.status==='arrived'?'📍':'📦'}
                </div>
                <div style={{flex:1,background:'#F9F8F5',borderRadius:8,padding:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <span style={{fontSize:11,fontWeight:700,color:STATUS_COLORS[u.status]?.color||'#556B2F',textTransform:'uppercase'}}>
                      {STATUS_COLORS[u.status]?.label||u.status}
                    </span>
                    <span style={{fontSize:11,color:'#8A8A8A'}}>{fmtDate(u.created_at)}</span>
                  </div>
                  <p style={{fontSize:13,color:'#2C1F16',margin:0}}>{u.message}</p>
                  {u.driver_name && (
                    <div style={{marginTop:6,fontSize:12,color:'#555'}}>
                      Driver: <strong>{u.driver_name}</strong> · {u.driver_phone}
                    </div>
                  )}
                  {u.updated_by_name && (
                    <div style={{marginTop:4,fontSize:11,color:'#8A8A8A'}}>By: {u.updated_by_name}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Update Modal */}
      {/* Order Ready Notification Modal */}
      {showReadyNotice && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:620}}>
            <div className="modal-hdr">
              <span className="modal-title">
                Order Ready Notification
              </span>
              <button
                className="btn btn-ghost btn-sm"
                disabled={readyBusy}
                onClick={() => setShowReadyNotice(false)}
              >
                X
              </button>
            </div>

            <div
              className="modal-body"
              style={{maxHeight:'75vh',overflowY:'auto'}}
            >
              <div style={{
                background:'#F9F8F5',
                border:'1px solid #E8E0D4',
                borderRadius:10,
                padding:14,
                marginBottom:14
              }}>
                <label style={{
                  display:'flex',
                  gap:10,
                  marginBottom:12,
                  fontSize:13
                }}>
                  <input
                    type="checkbox"
                    checked={
                      readyOptions.arrangement_confirmed
                    }
                    disabled={readyBusy}
                    onChange={event =>
                      updateReadyOption(
                        'arrangement_confirmed',
                        event.target.checked
                      )
                    }
                  />
                  <span>
                    <strong>
                      Customer confirmed delivery or pickup
                    </strong>
                    <br />
                    <span style={{
                      color:'#8A8A8A',
                      fontSize:11
                    }}>
                      Select only after customer confirmation.
                    </span>
                  </span>
                </label>

                {selected.delivery_type !== 'pickup' && (
                  <label style={{
                    display:'flex',
                    gap:10,
                    fontSize:13
                  }}>
                    <input
                      type="checkbox"
                      checked={
                        readyOptions.delivery_fee_paid
                      }
                      disabled={readyBusy}
                      onChange={event =>
                        updateReadyOption(
                          'delivery_fee_paid',
                          event.target.checked
                        )
                      }
                    />
                    <strong>
                      Delivery fee has been paid
                    </strong>
                  </label>
                )}
              </div>

              {readyBusy && !readyPreview ? (
                <div className="spinner" />
              ) : readyPreview ? (
                <>
                  <div style={{
                    display:'grid',
                    gridTemplateColumns:'1fr 1fr',
                    gap:10,
                    marginBottom:12
                  }}>
                    <div style={{
                      background:'#F2EFE7',
                      borderRadius:8,
                      padding:10,
                      fontSize:12
                    }}>
                      <div style={{
                        color:'#8A8A8A',
                        fontSize:10,
                        fontWeight:700
                      }}>
                        MESSAGE TYPE
                      </div>
                      <strong style={{
                        color:'#556B2F',
                        textTransform:'capitalize'
                      }}>
                        {readyPreview.notification_type
                          .replace(/_/g,' ')}
                      </strong>
                    </div>

                    <div style={{
                      background:'#F2EFE7',
                      borderRadius:8,
                      padding:10,
                      fontSize:12
                    }}>
                      <div style={{
                        color:'#8A8A8A',
                        fontSize:10,
                        fontWeight:700
                      }}>
                        BALANCE
                      </div>
                      <strong style={{
                        color:
                          readyPreview.outstanding_balance > 0
                            ? '#8F2E24'
                            : '#2E6B3A'
                      }}>
                        {fmt(
                          readyPreview.outstanding_balance
                        )}
                      </strong>
                    </div>
                  </div>

                  <div style={{
                    fontSize:12,
                    color:'#555',
                    lineHeight:1.8,
                    marginBottom:10
                  }}>
                    <div>
                      <strong>Order:</strong>{' '}
                      {readyPreview.order_number}
                    </div>
                    <div>
                      <strong>Email:</strong>{' '}
                      {readyPreview.recipient_email ||
                        'No valid email found'}
                    </div>
                    <div>
                      <strong>Phone:</strong>{' '}
                      {readyPreview.recipient_phone ||
                        'No phone found'}
                    </div>
                  </div>

                  <pre style={{
                    whiteSpace:'pre-wrap',
                    fontFamily:'inherit',
                    fontSize:13,
                    lineHeight:1.65,
                    border:'1px solid #E8E0D4',
                    borderRadius:10,
                    padding:16,
                    margin:0
                  }}>
                    {readyPreview.message}
                  </pre>

                  {readyPreview.notification_type ===
                    'outstanding_balance' && (
                    <div style={{
                      marginTop:12,
                      background:
                        readyPreview.online_payment_available &&
                        readyPreview.payment_url
                          ? '#E8F5EB'
                          : '#FFF3DC',
                      color:
                        readyPreview.online_payment_available &&
                        readyPreview.payment_url
                          ? '#2E6B3A'
                          : '#8B5E0A',
                      borderRadius:8,
                      padding:12,
                      fontSize:12
                    }}>
                      {readyPreview.online_payment_available &&
                      readyPreview.payment_url ? (
                        <>
                          <div style={{
                            fontWeight:700,
                            marginBottom:5
                          }}>
                            Secure payment link ready
                          </div>

                          <div style={{
                            lineHeight:1.6,
                            marginBottom:10
                          }}>
                            The link requests the full outstanding
                            balance. Bank transfer remains available
                            as an alternative.
                          </div>

                          <div style={{
                            display:'flex',
                            gap:8,
                            flexWrap:'wrap'
                          }}>
                            <a
                              className="btn btn-primary btn-sm"
                              href={readyPreview.payment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration:'none'
                              }}
                            >
                              Open Payment Page
                            </a>

                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={async () => {
                                try {
                                  await copyText(
                                    readyPreview.payment_url
                                  );

                                  alert(
                                    'Payment link copied.'
                                  );
                                } catch (error) {
                                  alert(
                                    'The payment link could not be copied. Open the payment page and copy it from the browser.'
                                  );
                                }
                              }}
                            >
                              Copy Payment Link
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{
                            fontWeight:700,
                            marginBottom:5
                          }}>
                            Bank transfer remains available
                          </div>

                          <div style={{lineHeight:1.6}}>
                            Online payment is not currently enabled.
                            The customer can use the bank details in
                            the message.
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div style={{
                    marginTop:12,
                    color:'#8A8A8A',
                    fontSize:11
                  }}>
                    The storage grace period starts only after
                    email acceptance or confirmed WhatsApp
                    sending.
                  </div>
                </>
              ) : null}
            </div>

            <div
              className="modal-ftr"
              style={{flexWrap:'wrap'}}
            >
              <button
                className="btn btn-outline"
                disabled={readyBusy}
                onClick={() => setShowReadyNotice(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-outline"
                disabled={
                  readyBusy ||
                  !readyPreview?.whatsapp_url
                }
                onClick={() => {
                  const opened = window.open(
                    readyPreview.whatsapp_url,
                    '_blank',
                    'noopener,noreferrer'
                  );

                  if (!opened) {
                    alert(
                      'Allow pop-ups and try WhatsApp again.'
                    );
                  }
                }}
              >
                Open WhatsApp
              </button>

              <button
                className="btn btn-outline"
                disabled={
                  readyBusy ||
                  !readyPreview?.whatsapp_url
                }
                onClick={confirmReadyWhatsApp}
                style={{
                  color:'#2E6B3A',
                  borderColor:'#2E6B3A'
                }}
              >
                Confirm WhatsApp Sent
              </button>

              <button
                className="btn btn-primary"
                disabled={
                  readyBusy ||
                  !readyPreview?.recipient_email
                }
                onClick={sendReadyEmail}
              >
                {readyBusy
                  ? 'Working...'
                  : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditDelivery && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-hdr">
              <span className="modal-title">Edit Delivery</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowEditDelivery(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Customer Name</label>
                  <input className="form-control" value={editForm.customer_name}
                    onChange={e=>setEditForm(f=>({...f,customer_name:e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Phone</label>
                  <input className="form-control" value={editForm.customer_phone}
                    onChange={e=>setEditForm(f=>({...f,customer_phone:e.target.value}))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Customer Email</label>
                <input className="form-control" value={editForm.customer_email}
                  onChange={e=>setEditForm(f=>({...f,customer_email:e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Address</label>
                <textarea className="form-control" rows={2} value={editForm.delivery_address}
                  onChange={e=>setEditForm(f=>({...f,delivery_address:e.target.value}))} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Delivery Type</label>
                  <select className="form-control" value={editForm.delivery_type}
                    onChange={e=>setEditForm(f=>({...f,delivery_type:e.target.value}))}>
                    <option value="public_transport">Public Transport</option>
                    <option value="fez">Fez Logistics</option>
                    <option value="same_city_rider">Same City Rider</option>
                    <option value="pickup">Pick Up</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fee Paid By</label>
                  <select className="form-control" value={editForm.delivery_fee_paid_by}
                    onChange={e=>setEditForm(f=>({...f,delivery_fee_paid_by:e.target.value}))}>
                    <option value="customer">Customer</option>
                    <option value="bloans">Bloans</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Balance Due from Customer</label>
                <input type="number" className="form-control" value={editForm.balance_amount}
                  onChange={e=>setEditForm(f=>({...f,balance_amount:e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={editForm.notes}
                  onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowEditDelivery(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async()=>{
                try {
                  const payload = {...editForm};
                  if (selected.dispatched_at || selected.delivered_at || selected.pickup_at ||
                      ['dispatched','out_for_delivery','awaiting_proof','awaiting_completion','ready_for_pickup','delivered','picked_up'].includes(selected.status)) {
                    const reason = window.prompt('Reason for editing this dispatched delivery (required if fees change):');
                    if (reason === null) return;
                    payload.fee_change_reason = reason.trim();
                  }
                  await put('/deliveries/'+selected.id, payload);
                  setShowEditDelivery(false);
                  loadDetail(selected.id);
                  load();
                } catch(e) { alert(e.message); }
              }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
      {showEditFee && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-hdr">
              <span className="modal-title">Update Fee & Driver Details</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowEditFee(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#FFF3DC',borderRadius:8,padding:10,marginBottom:14,fontSize:12,color:'#8B5E0A'}}>
                Update these after the package has been sent and the transport cost is confirmed.
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Delivery Fee (₦)</label>
                  <input type="number" className="form-control" value={feeForm.delivery_fee}
                    onChange={e=>setFeeForm(f=>({...f,delivery_fee:e.target.value}))}
                    placeholder="e.g. 2500" />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount Paid to Driver (₦)</label>
                  <input type="number" className="form-control" value={feeForm.driver_amount}
                    onChange={e=>setFeeForm(f=>({...f,driver_amount:e.target.value}))}
                    placeholder="e.g. 2000" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Fee Paid By</label>
                <select className="form-control" value={feeForm.delivery_fee_paid_by}
                  onChange={e=>setFeeForm(f=>({...f,delivery_fee_paid_by:e.target.value}))}>
                  <option value="customer">Customer</option>
                  <option value="bloans">Bloans</option>
                </select>
              </div>
              <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',margin:'12px 0 8px'}}>Driver / Rider Details</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Driver / Rider Name</label>
                  <input className="form-control" value={feeForm.rider_name}
                    onChange={e=>setFeeForm(f=>({...f,rider_name:e.target.value}))}
                    placeholder="e.g. Emeka" />
                </div>
                <div className="form-group">
                  <label className="form-label">Driver / Rider Phone</label>
                  <input className="form-control" value={feeForm.rider_phone}
                    onChange={e=>setFeeForm(f=>({...f,rider_phone:e.target.value}))}
                    placeholder="e.g. 08012345678" />
                </div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowEditFee(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async()=>{
                try {
                  const payload = {...feeForm};
                  if (selected.dispatched_at || selected.delivered_at || selected.pickup_at ||
                      ['dispatched','out_for_delivery','awaiting_proof','awaiting_completion','ready_for_pickup','delivered','picked_up'].includes(selected.status)) {
                    const reason = window.prompt('Reason for this delivery fee correction:');
                    if (!reason || !reason.trim()) return;
                    payload.fee_change_reason = reason.trim();
                  }
                  await put('/deliveries/'+selected.id, payload);
                  setShowEditFee(false);
                  loadDetail(selected.id);
                } catch(e) { alert(e.message); }
              }}>Save</button>
            </div>
          </div>
        </div>
      )}
      {showUpdate && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr">
              <span className="modal-title">Add Delivery Update</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowUpdate(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Status *</label>
                <select className={inp} value={updateForm.status} onChange={e=>{
                  setUpd('status',e.target.value);
                  setUpd('message', DEFAULT_MESSAGES[e.target.value]||'');
                }}>
                  <option value="">Select status...</option>
                  {(STATUS_OPTIONS[selected.delivery_type]||[]).map(s=>(
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Message to Customer *</label>
                <textarea className={inp} rows={3} value={updateForm.message}
                  onChange={e=>setUpd('message',e.target.value)} />
              </div>
              {updateForm.status==='with_driver' && (
                <>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Driver Name</label>
                      <input className={inp} value={updateForm.driver_name} onChange={e=>setUpd('driver_name',e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Driver Phone</label>
                      <input className={inp} value={updateForm.driver_phone} onChange={e=>setUpd('driver_phone',e.target.value)} /></div>
                  </div>
                </>
              )}
              {['picked_up','delivered'].includes(updateForm.status) && selected.delivery_type==='pickup' && (
                <>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Pick Up Person Name</label>
                      <input className={inp} value={updateForm.pickup_person_name} onChange={e=>setUpd('pickup_person_name',e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Pick Up Person Phone</label>
                      <input className={inp} value={updateForm.pickup_person_phone} onChange={e=>setUpd('pickup_person_phone',e.target.value)} /></div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowUpdate(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addUpdate} disabled={saving}>
                {saving?'Saving...':'Save Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">🚚 Deliveries</h2>
          <p className="text-muted text-sm">Manage and track all customer deliveries</p>
        </div>
        <button className="btn btn-primary" onClick={()=>setShowForm(true)}>+ New Delivery</button>
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <input className="form-control" placeholder="Search customer or tracking code..."
          value={search} onChange={e=>setSearch(e.target.value)} style={{width:260}} />
        <select className="form-control" style={{width:180}} value={filterType} onChange={e=>setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {Object.entries(DELIVERY_TYPES).map(([k,v])=>(
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select className="form-control" style={{width:180}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_COLORS).map(([k,v])=>(
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? <div className="spinner"/> : (
          <table>
            <thead><tr>
              <th>Tracking</th><th>Customer</th><th>Type</th>
              <th>Status</th><th>Contacted</th><th>Storage Fee</th><th>Created</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.length===0 ? (
                <tr><td colSpan="8"><div className="empty-state"><p>No deliveries found</p></div></td></tr>
              ) : filtered.map(d => {
                const contactedAt = d.customer_contacted_at ? new Date(d.customer_contacted_at) : null;
                const daysTotal = contactedAt ? Math.floor((new Date()-contactedAt)/(1000*60*60*24)) : 0;
                const billable = Math.max(0, daysTotal - 7);
                const fee = billable * 1000;
                return (
                  <tr key={d.id}>
                    <td style={{fontFamily:'monospace',fontSize:12,fontWeight:600,color:'#556B2F'}}>{d.tracking_code}</td>
                    <td style={{fontWeight:500}}>{d.customer_name}</td>
                    <td><span style={{fontSize:11,padding:'3px 8px',borderRadius:12,
                      background:DELIVERY_TYPES[d.delivery_type]?.bg,
                      color:DELIVERY_TYPES[d.delivery_type]?.color,fontWeight:600}}>
                      {DELIVERY_TYPES[d.delivery_type]?.label||d.delivery_type}
                    </span></td>
                    <td><span style={{fontSize:11,padding:'3px 8px',borderRadius:12,
                      background:STATUS_COLORS[d.status]?.bg,
                      color:STATUS_COLORS[d.status]?.color,fontWeight:600}}>
                      {STATUS_COLORS[d.status]?.label||d.status}
                    </span></td>
                    <td style={{fontSize:12,color:d.customer_contacted_at?'#2E6B3A':'#8A8A8A'}}>
                      {d.customer_contacted_at ? '✅ '+fmtDate(d.customer_contacted_at) : '—'}
                    </td>
                    <td style={{fontSize:12,color:fee>0?'#8F2E24':'#8A8A8A',fontWeight:fee>0?700:400}}>
                      {fee>0 ? fmt(fee) : contactedAt ? (daysTotal<=7?`${7-daysTotal}d grace`:'—') : '—'}
                    </td>
                    <td className="text-muted text-sm">{fmtDate(d.created_at)}</td>
                    <td><button className="btn btn-ghost btn-sm" onClick={()=>loadDetail(d.id)}>View</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New Delivery Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:680}}>
            <div className="modal-hdr">
              <span className="modal-title">New Delivery</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Customer Name *</label>
                  <input className={inp} value={form.customer_name} onChange={e=>set('customer_name',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Customer Email</label>
                  <input type="email" className={inp} value={form.customer_email} onChange={e=>set('customer_email',e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Customer Phone</label>
                  <input className={inp} value={form.customer_phone} onChange={e=>set('customer_phone',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Delivery Type *</label>
                  <select className={inp} value={form.delivery_type} onChange={e=>set('delivery_type',e.target.value)}>
                    {Object.entries(DELIVERY_TYPES).map(([k,v])=>(
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select></div>
              </div>
              <div className="form-group"><label className="form-label">Delivery Address</label>
                <textarea className={inp} rows={2} value={form.delivery_address} onChange={e=>set('delivery_address',e.target.value)} /></div>

              {/* Link to Invoice and Job Order */}
              <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',margin:'12px 0 8px'}}>Link to Records</div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Invoice</label>
                  <select className={inp} value={form.invoice_id} onChange={e=>{
                    set('invoice_id',e.target.value);
                    const inv = invoices.find(i=>i.id===e.target.value);
                    if (inv) {
                      set('customer_name', inv.customer_name||form.customer_name);
                      set('balance_amount', inv.balance_due||form.balance_amount);
                    }
                  }}>
                    <option value="">Select invoice (optional)</option>
                    {invoices.map(inv=>(
                      <option key={inv.id} value={inv.id}>
                        {inv.invoice_number} — {inv.customer_name} (₦{Number(inv.balance_due||0).toLocaleString('en-NG')} due)
                      </option>
                    ))}
                  </select></div>
                <div className="form-group"><label className="form-label">Job Order</label>
                  <select className={inp} value={form.job_order_id} onChange={e=>set('job_order_id',e.target.value)}>
                    <option value="">Select job order (optional)</option>
                    {jobOrders.map(jo=>(
                      <option key={jo.id} value={jo.id}>
                        {jo.job_number||jo.id.slice(0,8)} — {jo.customer_name||jo.description||'Job Order'}
                      </option>
                    ))}
                  </select></div>
              </div>

              {/* Package Details */}
              <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',margin:'12px 0 8px'}}>Package Details</div>
              <div className="form-group"><label className="form-label">Package Description</label>
                <input className={inp} value={form.package_description} onChange={e=>set('package_description',e.target.value)} placeholder="e.g. 3 handbags — olive green tote, brown clutch, cream crossbody" /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Weight</label>
                  <input type="number" className={inp} value={form.package_weight} onChange={e=>set('package_weight',e.target.value)} placeholder="0.00" /></div>
                <div className="form-group"><label className="form-label">Unit</label>
                  <select className={inp} value={form.package_weight_unit} onChange={e=>set('package_weight_unit',e.target.value)}>
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="lbs">lbs</option>
                  </select></div>
              </div>

              {/* Payment Details */}
              <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',margin:'12px 0 8px'}}>Payment</div>
              {form.delivery_type==='public_transport' && (
                <div style={{background:'#FFF3DC',borderRadius:8,padding:'10px 12px',marginBottom:10,fontSize:12,color:'#8B5E0A'}}>
                  For public transport — the delivery fee and driver amount are often negotiated at the motor park. You can leave these blank now and update them after the package is sent.
                </div>
              )}
              <div className="form-row">
                <div className="form-group"><label className="form-label">Balance Due from Customer</label>
                  <input type="number" className={inp} value={form.balance_amount} onChange={e=>set('balance_amount',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Delivery Fee</label>
                  <input type="number" className={inp} value={form.delivery_fee} onChange={e=>set('delivery_fee',e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Delivery Fee Paid By</label>
                  <select className={inp} value={form.delivery_fee_paid_by} onChange={e=>set('delivery_fee_paid_by',e.target.value)}>
                    <option value="customer">Customer</option>
                    <option value="bloans">Bloans</option>
                  </select></div>
                {['public_transport','same_city_rider'].includes(form.delivery_type) && (
                  <div className="form-group"><label className="form-label">Amount to Pay Driver/Rider</label>
                    <input type="number" className={inp} value={form.driver_amount} onChange={e=>set('driver_amount',e.target.value)} /></div>
                )}
              </div>

              {/* Fez Fields */}
              {form.delivery_type === 'fez' && (
                <>
                  <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',margin:'12px 0 8px'}}>Fez Logistics</div>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Fez Tracking Code</label>
                      <input className={inp} value={form.fez_tracking_code} onChange={e=>set('fez_tracking_code',e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Fez Tracking URL</label>
                      <input className={inp} value={form.fez_tracking_url} onChange={e=>set('fez_tracking_url',e.target.value)} /></div>
                  </div>
                </>
              )}

              {/* Rider Fields */}
              {form.delivery_type === 'same_city_rider' && (
                <>
                  <div style={{fontWeight:700,fontSize:12,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.06em',margin:'12px 0 8px'}}>Rider Details</div>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">Rider Name</label>
                      <input className={inp} value={form.rider_name} onChange={e=>set('rider_name',e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Rider Phone</label>
                      <input className={inp} value={form.rider_phone} onChange={e=>set('rider_phone',e.target.value)} /></div>
                  </div>
                </>
              )}

              <div className="form-group" style={{marginTop:8}}><label className="form-label">Notes</label>
                <textarea className={inp} rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} /></div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createDelivery} disabled={saving}>
                {saving?'Creating...':'Create Delivery'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function printWaybill(delivery, printerWidth) {
  const w = window.open('', '_blank');
  if (!w) {
    window.alert('The waybill print window was blocked. Allow pop-ups for BloansBooks, then click Print Waybill again.');
    return;
  }
  const escapePrintHtml = value => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  delivery = Object.fromEntries(Object.entries(delivery).map(([key,value]) =>
    [key, typeof value === 'string' && key !== 'created_at' ? escapePrintHtml(value) : value]
  ));
  const date = delivery.created_at ? new Date(delivery.created_at).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'}) : '';
  const typeLabel = {pending_arrangement:'Awaiting Delivery Arrangement',fez:'Fez Logistics',public_transport:'Public Transport',same_city_rider:'Same City Rider',customer_arranged_rider:'Customer-arranged Rider',pickup:'Pick Up'};
  w.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Waybill ${delivery.tracking_code}</title>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:Arial,Helvetica,sans-serif; width:74mm; max-width:74mm; margin:0 auto; padding:3mm; font-size:10.5pt; font-weight:700; line-height:1.3; color:#000000; background:#ffffff; }
  @media print {
    @page { size:80mm auto; margin:3mm; }
    * { color:#000000 !important; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
    body { margin:0; padding:0; }
  }
  .company-name { font-size:17pt; font-weight:900; text-align:center; display:block; }
  .doc-title { font-size:14pt; font-weight:900; text-align:center; display:block; margin:4px 0; }
  .section-title { font-size:11pt; font-weight:900; text-transform:uppercase; margin:8px 0 3px; border-bottom:2px solid #000000; padding-bottom:2px; display:block; }
  .label { font-size:10pt; font-weight:700; color:#000000; text-transform:uppercase; margin-top:5px; display:block; }
  .value { font-size:11pt; font-weight:700; color:#000000; word-wrap:break-word; display:block; }
  .value-lg { font-size:13pt; font-weight:900; color:#000000; word-wrap:break-word; display:block; }
  .divider { border-top:2px solid #000000; margin:6px 0; }
  .footer { font-size:9.5pt; font-weight:700; text-align:center; color:#000000; display:block; }
  .center { text-align:center; display:block; }
</style>
<script>window.onload=function(){window.print();}</script>
</head><body>
  <span class="company-name">BLOANS HANDBAGS</span>
  <span class="center" style="font-size:10pt;font-weight:700;">No. 3, S. Ade-Ojo Estate, Sawmill</span>
  <span class="center" style="font-size:10pt;font-weight:700;">Old Ife Road, Ibadan, Oyo State</span>
  <div class="divider"></div>

  <span class="doc-title">WAYBILL</span>
  <div class="divider"></div>

  <span class="section-title">Order / Tracking</span>
  <span class="value-lg">${delivery.tracking_code||'—'}</span>
  <span class="label">Date</span>
  <span class="value">${date}</span>
  <span class="label">Delivery Method</span>
  <span class="value">${typeLabel[delivery.delivery_type]||delivery.delivery_type||'—'}</span>

  <span class="section-title">Receiver Information</span>
  <span class="label">Customer Name</span>
  <span class="value-lg">${delivery.customer_name||'—'}</span>
  <span class="label">Phone Number</span>
  <span class="value-lg">${delivery.customer_phone||'—'}</span>
  <span class="label">Delivery Address</span>
  <span class="value">${delivery.delivery_address||'—'}</span>

  <span class="section-title">Package Information</span>
  <span class="label">Description</span>
  <span class="value">${delivery.package_description||'—'}</span>
  ${delivery.package_weight ? `<span class="label">Weight</span><span class="value">${delivery.package_weight} ${delivery.package_weight_unit||'kg'}</span>` : ''}
  ${delivery.special_instructions ? `<span class="label">Special Instructions</span><span class="value">${delivery.special_instructions}</span>` : ''}

  ${delivery.rider_name ? `<span class="section-title">Courier / Driver</span>
  <span class="label">Name</span><span class="value">${delivery.rider_name}</span>
  ${delivery.rider_phone ? `<span class="label">Phone</span><span class="value">${delivery.rider_phone}</span>` : ''}` : ''}

  <div class="divider"></div>
  <span class="footer">Thank you for choosing Bloans Handbags.</span>
  <span class="footer">www.bloanshandbags.com</span>
</body></html>`);
  w.document.close();
}
